import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2, MessageCircle, Send, X, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fetchComforttoolConfig, postSupportDeskChat, type SupportDeskMessage } from '../lib/api';
import { playReceiveSound, playSendSound } from './supportDeskSounds';

const STORAGE_KEY = 'comforttool_support_desk_v1';
const READ_ASSISTANT_KEY = 'comforttool_support_desk_read_assistant_v1';

const SUGGESTED_GUEST: string[] = [
  'Hoe start ik de scan?',
  'Wat is het verschil tussen Comfort Basis en Plus?',
  'Waar blijven mijn antwoorden opgeslagen?',
];

const SUGGESTED_AUTH: string[] = [
  'Hoe koppel ik mijn betaling aan mijn account?',
  'Waar vind ik mijn rapport na aankoop?',
  'Hoe kan ik na het rapport verder met productkeuze?',
];

function loadStored(): SupportDeskMessage[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const p = JSON.parse(raw) as unknown;
    if (!Array.isArray(p)) return [];
    const out: SupportDeskMessage[] = [];
    for (const m of p) {
      if (!m || typeof m !== 'object') continue;
      const role = (m as SupportDeskMessage).role;
      const content = (m as SupportDeskMessage).content;
      if ((role !== 'user' && role !== 'assistant') || typeof content !== 'string') continue;
      out.push({ role, content: content.slice(0, 8000) });
    }
    return out.slice(-24);
  } catch {
    return [];
  }
}

function initialReadAssistantCount(msgs: SupportDeskMessage[]): number {
  try {
    const raw = sessionStorage.getItem(READ_ASSISTANT_KEY);
    if (raw != null && raw !== '') {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) return n;
    }
  } catch {
    /* ignore */
  }
  return msgs.filter((m) => m.role === 'assistant').length;
}

function persistReadAssistantCount(n: number): void {
  try {
    sessionStorage.setItem(READ_ASSISTANT_KEY, String(n));
  } catch {
    /* ignore */
  }
}

export function SupportDeskChat() {
  const location = useLocation();
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [messages, setMessages] = useState<SupportDeskMessage[]>(() => loadStored());
  const [readAssistantCount, setReadAssistantCount] = useState(() => initialReadAssistantCount(loadStored()));
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorNeedsLogin, setErrorNeedsLogin] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const suggested = user ? SUGGESTED_AUTH : SUGGESTED_GUEST;

  const assistantCount = useMemo(() => messages.filter((m) => m.role === 'assistant').length, [messages]);
  const unreadAssistant = Math.max(0, assistantCount - readAssistantCount);
  const badgeCount = open ? 0 : Math.min(3, unreadAssistant);
  const showBadge = badgeCount > 0;

  useEffect(() => {
    void fetchComforttoolConfig().then((c) => setAvailable(c.support_desk_available === true));
  }, []);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  /** Terwijl het paneel open is: elk adviseur-antwoord telt als gelezen. */
  useEffect(() => {
    if (!open) return;
    if (readAssistantCount !== assistantCount) {
      setReadAssistantCount(assistantCount);
      persistReadAssistantCount(assistantCount);
    }
  }, [open, assistantCount, readAssistantCount]);

  const scrollToBottom = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    scrollToBottom();
  }, [messages, loading, open, scrollToBottom]);

  useEffect(() => {
    if (!open) return;
    const mq = window.matchMedia('(max-width: 639px)');
    const apply = () => {
      document.body.style.overflow = mq.matches ? 'hidden' : '';
    };
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      document.body.style.overflow = '';
    };
  }, [open]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setReadAssistantCount(0);
    persistReadAssistantCount(0);
    setError(null);
    setErrorNeedsLogin(false);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const send = async (text: string) => {
    const t = text.trim();
    if (!t || loading || available !== true) return;
    setInput('');
    setError(null);
    setErrorNeedsLogin(false);
    playSendSound();
    const nextUser: SupportDeskMessage[] = [...messages, { role: 'user', content: t }];
    setMessages(nextUser);
    setLoading(true);
    try {
      const r = await postSupportDeskChat(
        {
          messages: nextUser,
          page: location.pathname,
        },
        token
      );
      if (!r.ok) {
        if (r.error === 'support_desk_login_required') {
          setErrorNeedsLogin(true);
          setError(
            r.message ??
              'Log in om verder te chatten — dan kan de adviseur je beter helpen met account, aankopen en rapport.'
          );
          setMessages((prev) => prev.slice(0, -1));
          return;
        }
        if (r.error === 'support_desk_guest_quota') {
          setErrorNeedsLogin(true);
          setError(
            r.message ??
              'Je hebt vandaag het maximale aantal berichten zonder account gebruikt. Log in om verder te chatten, of gebruik contact.'
          );
          setMessages((prev) => prev.slice(0, -1));
          return;
        }
        const msg =
          r.error === 'rate_limited'
            ? 'Even geduld: er zijn te veel vragen vanaf dit apparaat. Probeer over een paar minuten opnieuw.'
            : r.error === 'support_desk_timeout'
              ? (r.message ??
                'Het duurde even te lang. Probeer het nog eens met een kortere vraag — of stuur een mail via contact.')
              : (r.message ?? 'Er ging iets mis. Probeer het later opnieuw.');
        setError(msg);
        setMessages((prev) => prev.slice(0, -1));
        return;
      }
      const reply = r.message;
      const pauseMs = Math.min(2400, 280 + Math.floor(reply.length * 5.5));
      await new Promise((res) => setTimeout(res, pauseMs));
      playReceiveSound();
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setError('Geen verbinding met de server. Controleer je internet en probeer opnieuw.');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const openPanel = () => {
    setOpen(true);
    const ac = messages.filter((m) => m.role === 'assistant').length;
    setReadAssistantCount(ac);
    persistReadAssistantCount(ac);
    setTimeout(() => inputRef.current?.focus(), 280);
  };

  return (
    <div data-support-desk>
      <button
        type="button"
        onClick={() => openPanel()}
        className="fixed bottom-[max(1rem,calc(0.75rem+env(safe-area-inset-bottom,0px)))] right-4 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-ct-accent text-white shadow-lg shadow-ct-accent/30 transition-transform duration-200 hover:scale-[1.03] hover:bg-blue-500 sm:bottom-6 sm:right-6"
        aria-label={
          showBadge
            ? `Open installatieadviseur, ${unreadAssistant} nieuw${unreadAssistant === 1 ? ' antwoord' : ' antwoorden'}`
            : 'Open installatieadviseur'
        }
      >
        <MessageCircle className="h-7 w-7" aria-hidden />
        {showBadge ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white shadow-md ring-2 ring-ct-surface"
            aria-hidden
          >
            {badgeCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[80] bg-black/60 max-sm:bg-black/55 max-sm:backdrop-blur-none sm:bg-black/45 sm:backdrop-blur-sm"
            aria-label="Sluit adviseur"
            onClick={() => setOpen(false)}
          />
          <div
            className="fixed z-[90] flex min-h-0 w-[min(400px,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-2xl border border-ct-border bg-ct-surface shadow-2xl
              max-sm:inset-x-3 max-sm:top-[max(5rem,calc(env(safe-area-inset-top,0px)+3.5rem))] max-sm:bottom-[max(6.5rem,calc(4.75rem+env(safe-area-inset-bottom,0px)))] max-sm:w-auto max-sm:max-h-none
              sm:bottom-24 sm:right-6 sm:left-auto sm:top-auto sm:h-[min(520px,calc(100dvh-5rem))] sm:max-h-[min(520px,calc(100dvh-5rem))]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="support-desk-title"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-ct-border bg-ct-elevated px-4 py-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-ct-accent/15 text-ct-accent">
                  <MessageCircle className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <h2 id="support-desk-title" className="truncate text-sm font-bold text-ct-text">
                    Installatieadviseur
                  </h2>
                  <p className="truncate text-xs text-ct-muted">
                    {user
                      ? 'Scan, rapport en volgende stappen — op basis van wat jij deelt'
                      : 'Korte antwoorden; log in voor persoonlijker advies'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 text-ct-muted transition hover:bg-ct-surface hover:text-ct-text"
                aria-label="Sluiten"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div
              ref={scrollAreaRef}
              className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-4"
            >
              {available === false ? (
                <div className="rounded-xl ct-caution-panel p-4 text-sm text-ct-muted">
                  De digitale adviseur is hier niet ingeschakeld op de server. Gebruik{' '}
                  <Link to="/help" className="font-semibold text-ct-accent hover:underline">
                    Help
                  </Link>
                  ,{' '}
                  <Link to="/troubleshooting" className="font-semibold text-ct-accent hover:underline">
                    Troubleshooting
                  </Link>{' '}
                  of{' '}
                  <Link to="/contact" className="font-semibold text-ct-accent hover:underline">
                    Contact
                  </Link>
                  .
                </div>
              ) : available === null ? (
                <p className="flex items-center gap-2 text-sm text-ct-muted" role="status">
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-ct-accent" aria-hidden />
                  Bezig met verbinden…
                </p>
              ) : (
                <>
                  {messages.length === 0 && !loading ? (
                    <div className="space-y-4">
                      <div className="rounded-xl border border-ct-border bg-ct-elevated/50 px-3 py-2.5 text-sm leading-relaxed text-ct-muted">
                        <p className="text-ct-text">
                          Hoi — ik help je met de scan, je uitkomsten, Comfort Basis of Plus, en hoe je dat deelt met
                          collega&apos;s op locatie of met je opdrachtgever.
                        </p>
                        <p className="mt-2">
                          Geen medisch of juridisch advies — bij twijfel op de bouwplaats: een professional ter plaatse of{' '}
                          <Link to="/contact" className="font-semibold text-ct-accent hover:underline">
                            contact
                          </Link>
                          .
                        </p>
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-ct-muted">Suggesties</p>
                      <div className="flex flex-wrap gap-2">
                        {suggested.map((q) => (
                          <button
                            key={q}
                            type="button"
                            onClick={() => void send(q)}
                            disabled={loading}
                            className="ct-hover-surface rounded-full border border-ct-border bg-ct-elevated/80 px-3 py-1.5 text-left text-xs font-medium text-ct-text hover:border-ct-accent/40 disabled:opacity-50"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {messages.map((m, i) => (
                    <div
                      key={`${i}-${m.role}-${m.content.slice(0, 24)}`}
                      className={`flex w-full ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[90%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${
                          m.role === 'user'
                            ? 'bg-ct-accent text-white'
                            : 'border border-ct-border bg-ct-elevated text-ct-text'
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {loading ? (
                    <div className="flex justify-start">
                      <div className="flex items-center gap-2 rounded-xl border border-ct-border bg-ct-elevated px-3 py-2 text-sm text-ct-muted">
                        <Loader2 className="h-4 w-4 animate-spin text-ct-accent" aria-hidden />
                        Even nadenken…
                      </div>
                    </div>
                  ) : null}
                  {error ? (
                    <div
                      className="space-y-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300"
                      role="alert"
                    >
                      <p>{error}</p>
                      {errorNeedsLogin ? (
                        <Link
                          to={`/inloggen?next=${encodeURIComponent(location.pathname + location.search)}`}
                          className="inline-block font-semibold underline"
                        >
                          Naar inloggen
                        </Link>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {available === true ? (
              <div className="shrink-0 space-y-2 border-t border-ct-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={clearChat}
                    disabled={loading || messages.length === 0}
                    className="inline-flex items-center gap-1 text-xs font-medium text-ct-muted transition hover:text-ct-accent disabled:opacity-40"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    Nieuw gesprek
                  </button>
                  <Link to="/contact" className="text-xs font-medium text-ct-accent hover:underline">
                    Mail het team →
                  </Link>
                </div>
                <p className="text-[10px] leading-snug text-ct-muted">
                  Indicatieve informatie; geen officiële berekening of rechtsadvies. Zie ook{' '}
                  <Link to="/disclaimer" className="underline">
                    disclaimer
                  </Link>
                  .
                </p>
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void send(input);
                  }}
                >
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Stel je vraag…"
                    disabled={loading}
                    className="min-w-0 flex-1 rounded-xl border border-ct-border bg-ct-bg px-3 py-2.5 text-sm text-ct-text placeholder:text-ct-muted focus:border-ct-accent focus:outline-none"
                    enterKeyHint="send"
                    autoComplete="off"
                  />
                  <button
                    type="submit"
                    disabled={loading || !input.trim()}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ct-accent text-white transition-colors hover:bg-blue-500 disabled:opacity-40"
                    aria-label="Verstuur"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
